import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type { SeoTagsPayload } from '@wix/native-components-infra/dist/src/types/types';
import {
  createPageUrl,
  getLastPage,
  getMultilingualQueryParam,
  SECTION_HOMEPAGE,
  urijs,
} from '@wix/communities-blog-client-common';
import { type PlatformApi } from '@app/external/common/controller/platform-api';
import {
  getEntityCount,
  getPaginationPageSize,
  getShowPagination,
} from '@app/external/common/selectors/pagination-selectors';
import { getTopology } from '@app/external/common/store/topology/topology-selectors';
import { type AppState } from '@app/external/common/types';
import { getPaginationUrls } from './get-pagination-item-data';

type GenerateFeedSEOTagsParams = {
  state: AppState;
  t: TFunction;
  platformApi: PlatformApi;
  initialSiteTitle: string;
  page?: number;
};

type FeedSeoLink = PlatformApi['seo']['links'][number];
type FeedSeoMetaTag = PlatformApi['seo']['metaTags'][number];

type FeedSeoDataTag =
  | { type: 'title'; children: string }
  | { type: 'link'; props: FeedSeoLink }
  | { type: 'meta'; props: FeedSeoMetaTag };

export const generateFeedSEOTags = ({
  state,
  initialSiteTitle,
  platformApi,
  page = 1,
  t,
}: GenerateFeedSEOTagsParams): SeoTagsPayload => {
  const canonicalUrl = prepareCanonicalUrl({ page, platformApi, state });
  const preparedTitle = prepareTitle({ initialSiteTitle, page, state, t });
  const links = prepareLinks({ canonicalUrl, page, platformApi, state });
  const metaTags = prepareMetaTags({ canonicalUrl, platformApi, preparedTitle });
  const topology = getTopology(state);
  const resolvedCanonicalUrl = links.find((link) => link.rel === 'canonical')?.href ?? canonicalUrl;

  return {
    itemType: ITEM_TYPES.STATIC_PAGE_V2,
    itemData: {
      context: {
        pageName: preparedTitle,
        defaultUrl: resolvedCanonicalUrl,
        isHomePage: topology.isHomePage,
        nextLink: links.find((link) => link.rel === 'next')?.href ?? '',
        prevLink: links.find((link) => link.rel === 'prev')?.href ?? '',
      },
    },
    seoData: {
      tags: buildFeedSeoDataTags({ preparedTitle, links, metaTags }),
    },
  };
};

function buildFeedSeoDataTags({
  preparedTitle,
  links,
  metaTags,
}: {
  preparedTitle: string;
  links: FeedSeoLink[];
  metaTags: FeedSeoMetaTag[];
}): FeedSeoDataTag[] {
  const tags: FeedSeoDataTag[] = [{ type: 'title', children: preparedTitle }];

  links.forEach((link) => {
    if (link.href) {
      tags.push({ type: 'link', props: link });
    }
  });

  metaTags.forEach((metaTag) => {
    if (metaTag.content) {
      tags.push({ type: 'meta', props: metaTag });
    }
  });

  return tags;
}

type PrepareCanonicalUrlParams = {
  state: AppState;
  page: number;
  platformApi: PlatformApi;
};

function prepareCanonicalUrl({ state, page, platformApi }: PrepareCanonicalUrlParams) {
  const topology = getTopology(state);
  const multilingualQueryParam = getMultilingualQueryParam(platformApi.window.multilingual);
  let url = topology.sectionUrl!;
  if (page === 1) {
    url = topology.isHomePage ? topology.baseUrl! : topology.sectionUrl!;
  } else {
    url = createPageUrl(page, topology.sectionUrl!);
  }

  return `${url}${multilingualQueryParam}`;
}

type TitleParams = {
  state: AppState;
  initialSiteTitle: string;
  page: number;
  t: TFunction;
};

function prepareTitle({ state, page, initialSiteTitle, t }: TitleParams): string {
  const lastPage = getLastPage(
    getEntityCount(state, 'posts'),
    getPaginationPageSize(state, SECTION_HOMEPAGE),
  );

  if (getShowPagination(state, SECTION_HOMEPAGE)) {
    if (page < 2) {
      return initialSiteTitle;
    }

    return `${initialSiteTitle} | ${t('pagination.current-position-tile', {
      page,
      lastPage,
    })}`;
  }

  return initialSiteTitle;
}

type MetaTagsParams = {
  preparedTitle: string;
  platformApi: PlatformApi;
  canonicalUrl: string;
};

function prepareMetaTags({
  platformApi,
  preparedTitle,
  canonicalUrl,
}: MetaTagsParams): PlatformApi['seo']['metaTags'] {
  let hasOpenGraphUrl = false;

  const preparedMetaTags = platformApi.seo.metaTags.map((tag) => {
    if (tag.property === 'og:title' || tag.name === 'twitter:title') {
      return { ...tag, content: preparedTitle };
    }

    if (tag.property === 'og:url') {
      hasOpenGraphUrl = true;
      return { ...tag, content: urijs(tag.content).readable() };
    }

    return tag;
  });

  if (!hasOpenGraphUrl) {
    preparedMetaTags.push({
      property: 'og:url',
      content: canonicalUrl,
      name: '',
    });
  }

  return preparedMetaTags;
}

type LinksParams = {
  state: AppState;
  platformApi: PlatformApi;
  canonicalUrl: string;
  page: number;
};

function prepareLinks({
  canonicalUrl,
  page,
  platformApi,
  state,
}: LinksParams): PlatformApi['seo']['links'] {
  const lastPage = getLastPage(
    getEntityCount(state, 'posts'),
    getPaginationPageSize(state, SECTION_HOMEPAGE),
  );

  const topology = getTopology(state);
  const preparedLinks = [...platformApi.seo.links];
  const paginationUrls = getPaginationUrls(
    topology.sectionUrl,
    page,
    lastPage,
    state,
    getMultilingualQueryParam(platformApi.window.multilingual),
  );

  if (preparedLinks.every((item) => item.rel !== 'canonical')) {
    preparedLinks.push({ rel: 'canonical', href: normalizeCanonicalHref(canonicalUrl) });
  }
  if (paginationUrls.prevUrl) {
    preparedLinks.push({ rel: 'prev', href: paginationUrls.prevUrl });
  }
  if (paginationUrls.nextUrl) {
    preparedLinks.push({ rel: 'next', href: paginationUrls.nextUrl });
  }

  return preparedLinks.map((link) =>
    link.rel === 'canonical' ? { ...link, href: normalizeCanonicalHref(link.href) } : link,
  );
}

function normalizeCanonicalHref(url: string) {
  return url.replace(/\/$/, '') || url;
}
